import type { ModuleKey } from '@ctcells/core'

/**
 * 쿼리 키 규칙 — **이 파일이 유일한 출처다.** 호출부에서 배열을 손으로 만들지 않는다.
 *
 * ```
 * ['l1', <도메인>, 'list', <필터 객체>]     통합 콘솔 목록
 * ['l1', <도메인>, 'detail', <id>]          통합 콘솔 상세
 * ['local', <모듈키>, <도메인>, …]          모듈 로컬 UI
 * ```
 *
 * 🔴 **첫 조각이 `l1` / `local` 로 갈린다.** 모듈 로컬은 다른 서버 · 다른 계약이고
 *    `allowed_commands` 조차 다른 값이다. 섞이면 캐시가 오염된다.
 *
 * 🔴 무효화는 **접두 일치**로 걸린다. `keys.batches.all()` 을 무효화하면 목록·상세가 같이 빠진다.
 *    구독 이벤트 → 무효화 매핑(`stream/bridge.ts`)이 이 성질에 기댄다.
 */

/**
 * 목록 필터 한 덩어리. 도메인마다 모양이 달라서 구조를 강제하지 않는다 —
 * **키의 동일성만** 본다(TanStack 이 구조 비교로 캐시를 가른다).
 * ⚠ 인터페이스는 인덱스 시그니처를 만족하지 않아 `object` 로 받는다. 값은 직렬화 가능해야 한다.
 */
export type ListFilter = object

const L1 = 'l1' as const

function domain(name: string) {
  return {
    all: () => [L1, name] as const,
    list: (filter?: ListFilter) => [L1, name, 'list', filter ?? {}] as const,
    detail: (id: string) => [L1, name, 'detail', id] as const,
  }
}

export const keys = {
  auth: {
    all: () => [L1, 'auth'] as const,
    sessions: (filter?: ListFilter) => [L1, 'auth', 'sessions', filter ?? {}] as const,
  },
  batches: domain('batches'),
  samples: domain('samples'),
  recipes: domain('recipes'),
  modules: domain('modules'),
  alarms: domain('alarms'),
  users: {
    ...domain('users'),
    /**
     * `GET /users/{id}/session-policy` 한 계정의 실효 세션 만료(`queries/admin.ts` `useUserSessionPolicy`).
     * 🔴 `users` 아래에 둔다 — 역할 변경(`useUpdateUser` 가 `users.all()` 을 무효화)이 «역할 단» 출처를 바꾸기 때문이다.
     */
    sessionPolicy: (userId: string) => [L1, 'users', 'session-policy', userId] as const,
  },
  /**
   * 관리 › 설정의 정책 셋(`queries/settingsPolicies.ts` · admin 한정 · IFC-RO-87·90·92).
   * 세션 정책 쓰기는 `users.all()` 도 같이 무효화한다 — 계정 상세의 실효 값이 그 표에서 나온다.
   */
  policies: {
    all: () => [L1, 'policies'] as const,
    session: () => [L1, 'policies', 'session'] as const,
    security: () => [L1, 'policies', 'security'] as const,
    controlLock: () => [L1, 'policies', 'control-lock'] as const,
  },
  logs: domain('logs'),
  history: domain('history'),
  exports: domain('exports'),
  reports: domain('reports'),
  loads: domain('loads'),
  unloads: domain('unloads'),
  reagentLots: domain('reagent-lots'),
  wasteContainers: domain('waste-containers'),
  envSensors: domain('env-sensors'),
  calibrations: domain('calibrations'),
  dispenseRecords: domain('dispense-records'),
  /**
   * `GET /batches/{id}/tracking` — 배치 추적(트레이 위치 · 챔버 상태 · `queries/ops.ts` `useBatchTracking`).
   * 구독 `batch_tracking` 프레임이 그 배치의 `detail(id)` 를 다시 받게 한다(`stream/bridge.ts`).
   * ⚠ 자원 점유(`resource-locks`)는 서버 v0.9.4 에서 걷혔다 — 이 키가 「이 배치가 6축 이송 중인가」의 근거를 이어받았다.
   */
  tracking: domain('batch-tracking'),
  /** `GET /monitoring/trays` — 트레이(C·A·L) · 장비 안 챔버 뷰(정본 선행 · `queries/monitoringTrays.ts`) */
  monitoringTrays: domain('monitoring-trays'),
  /* 레시피 v2 템플릿(`/templates/{kind}` · 표준 레시피) — 종류는 목록 필터(`kind`)가 가른다 */
  templates: domain('templates'),
  /** `GET /settings/alarm-recipients` 알림 수신자(admin · `queries/alarmRecipients.ts`) — 쓰기 뒤 `all()` 로 다시 받는다 */
  alarmRecipients: domain('alarm-recipients'),
  alarmRemote: domain('alarm-remote'),
  /**
   * 브로커 계정 · 모듈 브로커 접속(admin · `queries/broker.ts`) — 쓰기 뒤와 구독 `broker_change` 에 `all()` 로 다시 받는다.
   * 🔴 모듈 접속(`GET /modules/{id}/broker`)도 **여기 아래**다 — `modules.all()` 무효화(모듈 상태 프레임)마다 모듈에
   *    HTTP 를 다시 쏘지 않게.
   */
  broker: {
    all: () => [L1, 'broker'] as const,
    accounts: () => [L1, 'broker', 'accounts'] as const,
    core: () => [L1, 'broker', 'core'] as const,
    history: (name: string) => [L1, 'broker', 'history', name] as const,
    moduleConnection: (moduleId: string) => [L1, 'broker', 'module', moduleId] as const,
  },
  /**
   * `GET /consumable-replenishments` 소모품 보충 이력(`queries/records.ts`) — 보충 기록 쓰기(`commands/admin.ts`)가 `all()` 로 비운다.
   * 🔴 `modules` 아래에 두지 않는다 — 모듈 상태 프레임마다 `modules.all()` 이 무효화돼 이력 200줄을 다시 긁게 된다.
   */
  replenishments: domain('consumable-replenishments'),
  /**
   * `GET /waste-replacements` 폐기통 교체 이력(`queries/records.ts`). 🔴 `wasteContainers` 접두 아래다 — 교체 기록 쓰기
   * (`useRecordWasteReplacement` 가 `wasteContainers.all()` 을 비운다)에 같이 빠진다.
   */
  wasteReplacements: (filter?: ListFilter) =>
    [L1, 'waste-containers', 'replacements', filter ?? {}] as const,
  safety: {
    all: () => [L1, 'safety'] as const,
    state: () => [L1, 'safety', 'state'] as const,
    /** `GET /safety/stops` 걸린 것 목록(`queries/stops.ts`) — `safety.all()` 무효화에 같이 빠진다 */
    stops: () => [L1, 'safety', 'stops'] as const,
  },
  /**
   * 위치 등록부 · 모듈 위치 티칭(`queries/positions.ts` · `commands/positions.ts` · IFC-RO-95~102).
   * 🔴 **`modules` 접두 밖에 둔다** — 모듈 위치 · 자세 · 환산은 모듈에 HTTP 를 중계한다. `modules.all()`(모듈 상태 프레임마다) 아래면
   *    배치 중 어느 모듈의 사건이든 모듈에 다시 쏜다(`moduleControl` 과 같은 사고). 위치 목록은 그 모듈의 `version` 을 키에 실어
   *    그 모듈이 바뀔 때만 한 번 받는다(가드의 상태 · 제어 주체 축).
   * ⚠ 티칭 쓰기 뒤에는 `moduleAll(id)`(목록 · 환산)과 `registry()`(등록부 `taught` 칸)를 비운다.
   */
  positions: {
    all: () => [L1, 'positions'] as const,
    registry: () => [L1, 'positions', 'registry'] as const,
    moduleAll: (moduleId: string) => [L1, 'positions', 'module', moduleId] as const,
    module: (moduleId: string, version: number | undefined) =>
      [L1, 'positions', 'module', moduleId, 'list', version ?? null] as const,
    resolve: (moduleId: string, positionId: string, query: Readonly<Record<string, string>>) =>
      [L1, 'positions', 'module', moduleId, 'resolve', positionId, query] as const,
    pose: (moduleId: string) => [L1, 'positions', 'pose', moduleId] as const,
  },
  systemMode: {
    all: () => [L1, 'system-mode'] as const,
    current: () => [L1, 'system-mode', 'current'] as const,
  },
  health: {
    all: () => [L1, 'health'] as const,
    live: () => [L1, 'health', 'live'] as const,
  },

  /**
   * 모듈 로컬(L3). ⚠ 아직 아무도 쓰지 않는다 — PIN 로그인 API 가 계약에 없다.
   * 네임스페이스만 미리 가른다.
   */
  local: (moduleKey: ModuleKey) => ({
    all: () => ['local', moduleKey] as const,
    status: () => ['local', moduleKey, 'status'] as const,
  }),
} as const
